import { useLocation } from '@reach/router'
import { useCallback, useMemo } from 'react'

import type { ResolvedAddress } from '../components/CheckoutDialog/schema'

interface ConnectModuleTrackEventOptions {
  skill_id: number
  skill_name: string
  postal_code: string
  market_exists: boolean
  market_id?: string | undefined
}

interface UnsupportedMarketEvent {
  email: string
  skillGroup: string
  businessName: string
}

export function trackUnsupportedMarketEvent({
  email,
  skillGroup,
  businessName,
}: UnsupportedMarketEvent) {
  window.analytics?.track('Gigpro Lead Submission', {
    business: businessName,
    email,
    environment: 'production',
    market_id: 65,
    market_name: 'Other',
    lead_source: 'Marketing Lead',
    referral_source: 'find-a-pro',
    skill_group: skillGroup,
  })
}

export function trackFindProsEvent({
  skill_id,
  skill_name,
  postal_code,
  market_exists,
  market_id,
}: ConnectModuleTrackEventOptions) {
  window.analytics?.track('Find Pros Submission', {
    skill_id,
    skill_name,
    postal_code,
    market_exists,
    market_id,
  })
}

interface FindGigsTrackEventOptions {
  skill_id: number
  skill_name: string
  postal_code: string
  market_exists: boolean
  market_id?: string
}

export function trackFindGigsEvent({
  skill_id,
  skill_name,
  postal_code,
  market_exists,
  market_id,
}: FindGigsTrackEventOptions) {
  window.analytics?.track('Find Gigs Submission', {
    skill_id,
    skill_name,
    postal_code,
    market_exists,
    market_id,
  })
}

interface DownloadAppTrackEventOptions {
  path: string
  referrer: string
  platform: 'ios' | 'android'
}

export function trackDownloadAppEvent({
  path,
  referrer,
  platform,
}: DownloadAppTrackEventOptions) {
  window.analytics?.track('Download App Click', {
    path,
    referrer,
    platform,
  })
}

const referralSources = [
  'Atlas MKE',
  'Sysco',
  'Referral',
  'ENT Generated',
  'EMAIL Drip',
  'Organic',
  'Direct Mail',
  'Call Center',
  'Inside Referral',
  'revtron generated',
  'Norman Van Aken',
  'Trade Show',
  'Association',
  'Event Ad',
  'LinkedIn Paid',
  'find-a-pro',
] as const
type ReferralSource = (typeof referralSources)[number]

interface GigproLeadSubmissionProperties {
  /**
   * Required Properties
   */
  email: string
  business: string
  market_id: number

  /**
   * Should be provided if available
   */
  referral_source?: ReferralSource
  lead_source?: string
  lead_source_detail?: string

  /**
   * Optional Properties
   */
  first_name?: string
  last_name?: string
  phone_number?: string
  position?: string
  skill_group?: string
  market?: string
}

function isReferralSourceValid(
  source: string | null,
): source is ReferralSource {
  return source ? referralSources.includes(source as ReferralSource) : false
}

/**
 * Resolve lead source with URL params taking precedence over the fallback.
 * Mirrors useTrackGigproLeadSubmission: a valid referral_source wins, then
 * utm_medium, then the caller's fallback.
 */
function resolveLeadSource(fallback: string): string {
  if (typeof window === 'undefined') return fallback
  const params = new URLSearchParams(window.location.search)
  const referralSource = params.get('referral_source')
  if (isReferralSourceValid(referralSource)) return referralSource
  return params.get('utm_medium') || fallback
}

export function trackConnectFormSubmissionError(reason: string) {
  // @ts-expect-error analytics may not be defined
  window.heap?.track('Connect Form - Submission Error', {
    errorMessage: reason,
  })
}

interface QwickGoMarketProps {
  market_id?: number
  market?: string
}

interface QwickGoPositionAddedEvent extends QwickGoMarketProps {
  skill_id?: number
  role: string
  qty: number
  hrs: number
  rate?: number
}

export function trackQwickGoPositionAdded(props: QwickGoPositionAddedEvent) {
  window.analytics?.track('QwickGo Position Added to Cart', props)
}

interface QwickGoCheckoutOpenedEvent extends QwickGoMarketProps {
  item_count: number
  position_count: number
  line_items: Array<{ role: string; qty: number; hrs: number }>
}

export function trackQwickGoCheckoutOpened(props: QwickGoCheckoutOpenedEvent) {
  window.analytics?.track('QwickGo Checkout Opened', props)
}

interface QwickGoCheckoutContactSubmittedEvent extends QwickGoMarketProps {
  session_id: string
  first_name: string
  last_name: string
  email: string
  phone_number: string
  location_name: string
  is_existing_location_match: boolean
  address?: ResolvedAddress | null
}

export function trackQwickGoCheckoutContactSubmitted({
  address,
  ...props
}: QwickGoCheckoutContactSubmittedEvent) {
  window.analytics?.track('QwickGo Checkout Contact Submitted', {
    ...props,
    lead_source: resolveLeadSource('QwickGo'),
    address: address && {
      ...address,
      street: address.street_address,
      state: address.state_code,
    },
  })
}

interface QwickGoLeadIdentifyTraits extends QwickGoMarketProps {
  first_name: string
  last_name: string
  email: string
  phone_number: string
  location_name: string
  address?: ResolvedAddress
}

// Traits-only: no external_id until the real Qwick id arrives at checkout completion.
export function identifyQwickGoLead({
  first_name,
  last_name,
  email,
  phone_number,
  location_name,
  market_id,
  address,
}: QwickGoLeadIdentifyTraits) {
  window.analytics?.identify({
    firstName: first_name,
    lastName: last_name,
    email,
    phoneNumber: phone_number,
    locationName: location_name,
    leadSource: resolveLeadSource('QwickGo'),
    marketId: market_id,
    address: address && {
      ...address,
      street: address.street_address,
      state: address.state_code,
      postalCode: address.postal_code,
    },
  })
}

export function identifyQwickGoUser(externalId: string, market_id?: number) {
  window.analytics?.identify(externalId, { marketId: market_id })
}

interface QwickGoCheckoutShiftSubmittedEvent extends QwickGoMarketProps {
  session_id: string
  date: string
  item_count: number
  shifts: Array<{
    role: string
    start_time: string
    duration_hours: number
  }>
}

export function trackQwickGoCheckoutShiftSubmitted(
  props: QwickGoCheckoutShiftSubmittedEvent,
) {
  window.analytics?.track('QwickGo Checkout Shift Information Submitted', props)
}

interface QwickGoCheckoutPaymentAuthorizedEvent extends QwickGoMarketProps {
  session_id: string
  wages?: number
  platform_fee?: number
  total?: number
}

export function trackQwickGoCheckoutPaymentAuthorized(
  props: QwickGoCheckoutPaymentAuthorizedEvent,
) {
  window.analytics?.track('QwickGo Checkout Payment Method Authorized', props)
}

interface QwickGoCheckoutCompletedEvent extends QwickGoMarketProps {
  session_id: string
  external_id: string
  organization_id: string
  location_id: number
  gig_external_ids: string[]
  gig_count: number
  wages?: number
  date: string
}

export function trackQwickGoCheckoutCompleted(
  props: QwickGoCheckoutCompletedEvent,
) {
  window.analytics?.track('QwickGo Checkout Completed', props)
}

interface QwickGoCheckoutPhoneConflictEvent extends QwickGoMarketProps {
  session_id: string
  phone_number: string
  account_type?: string
}

export function trackQwickGoCheckoutPhoneConflict(
  props: QwickGoCheckoutPhoneConflictEvent,
) {
  window.analytics?.track('QwickGo Checkout Phone Conflict', props)
}

export function useTrackGigproLeadSubmission() {
  const location = useLocation()
  const params = useMemo(() => {
    return new URLSearchParams(location.search)
  }, [location.search])

  return useCallback(
    (
      lead: Omit<
        GigproLeadSubmissionProperties,
        'referral_source' | 'lead_source'
      >,
    ) => {
      let referral_source
      let lead_source
      let lead_source_detail

      /**
       * Derive referral and lead source from url parameters
       * and other business logic.
       *
       * See https://sidegigworks.slack.com/archives/C085QE6EGJX/p1748210716990349?thread_ts=1748194467.722319&cid=C085QE6EGJX
       */
      const referralSourceFromSearch = params.get('referral_source')

      if (isReferralSourceValid(referralSourceFromSearch)) {
        /**
         * Referral source is a valid value from the url parameters.
         * Use it as both referral_source and lead_source.
         */
        referral_source = referralSourceFromSearch
        lead_source = referralSourceFromSearch
      } else {
        lead_source = params.get('utm_medium')
      }

      lead_source_detail = params.get('utm_source')

      // construct lead source properties
      const properties: GigproLeadSubmissionProperties = {
        ...lead,
      }

      if (referral_source) {
        properties.referral_source = referral_source
      }
      if (lead_source) {
        properties.lead_source = lead_source
      }

      if (lead_source_detail) {
        properties.lead_source_detail = lead_source_detail
      }

      window.analytics?.track('Gigpro Lead Submission', properties)
    },
    [params],
  )
}
